import { saveAs } from 'file-saver';
import { withBusyCursor } from '@zawarski/palmetto-typescript-data-grid';
import { DOCUMENTS_API } from '@constants/index';

/**
 * palmetto-serverless-documents (the legacy Excel / Word reports). The service
 * still authenticates with the loopback access-token id (the `accessToken` the
 * portal returns with the verified account - legacy `userAuthCreds.id`), NOT
 * the Cognito id_token. The legacy axios client sent it both as the
 * `Authorization` header and as the `access_token` query parameter; every call
 * here carries both. The cursor shows busy while a report is being generated.
 */

/** `${DOCUMENTS_API}/documents/rep/<path>?access_token=<token>` (extra params kept). */
export const documentsUrl = (path: string, accessTokenId: string): string => {
  const url = new URL(`${DOCUMENTS_API}/documents/rep/${path.replace(/^\/+/, '')}`);
  if (accessTokenId) url.searchParams.set('access_token', accessTokenId);
  return url.toString();
};

const authHeaders = (accessTokenId: string): Record<string, string> =>
  accessTokenId ? { Authorization: accessTokenId } : {};

const fileNameFrom = (res: Response, fallback: string) => {
  const disposition = res.headers.get('content-disposition') || '';
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
  return match ? decodeURIComponent(match[1].trim()) : fallback;
};

/**
 * Excel report (legacy `HandleDownloadingExcelFile`): GET the report and save
 * it as `<fileStem>.xlsx` (or the server's file name). Throws on a non-2xx reply.
 */
export const downloadExcelReport = (path: string, fileStem: string, accessTokenId: string) =>
  withBusyCursor(async () => {
    const res = await fetch(documentsUrl(path, accessTokenId), {
      headers: {
        ...authHeaders(accessTokenId),
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    saveAs(await res.blob(), fileNameFrom(res, `${fileStem}.xlsx`));
  });

/**
 * EOPA report (legacy EOPAView `handlePrintDocument` -> `downloadDocument`):
 * a POST to `eopa/<id>` whose JSON body is `{ exerciseID }`, answered with the
 * generated document (saved under the server's file name, legacy fallback
 * "document").
 */
export const downloadEopaReport = (exerciseId: number, accessTokenId: string) =>
  withBusyCursor(async () => {
    const res = await fetch(documentsUrl(`eopa/${exerciseId}`, accessTokenId), {
      method: 'POST',
      headers: {
        ...authHeaders(accessTokenId),
        'Content-Type': 'application/json',
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      body: JSON.stringify({ exerciseID: exerciseId }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    saveAs(await res.blob(), fileNameFrom(res, 'document'));
  });

/**
 * ALOC letter report (legacy `printALOCReport`): POST the letter summary, the
 * service answers `{ url, name }` of the generated document, which is then
 * opened as a download.
 */
export const downloadAlocReport = (payload: Record<string, unknown>, accessTokenId: string) =>
  withBusyCursor(async () => {
    const res = await fetch(documentsUrl('aloc', accessTokenId), {
      method: 'POST',
      headers: { ...authHeaders(accessTokenId), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = (await res.json()) as { url?: string; name?: string };
    if (!body.url) throw new Error('No document URL in the reply');
    const anchor = document.createElement('a');
    anchor.download = body.name || 'aloc-report';
    anchor.href = body.url;
    anchor.click();
  });
